import React, { useEffect } from 'react';
import { Modal, Input, Button, Form, DatePicker, TimePicker, Select } from 'antd';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { createBooking, updateBooking, deleteBooking, fetchBookingById } from '../../state/actions/booking';
import dayjs from 'dayjs';

const { TextArea } = Input;

const AddBookingModal = ({
  isVisible,
  onCancel,
  onSubmit,
  selectedRoom = null,
  selectedDate = null,
  selectedHour = null,
  rooms = [],
  editingBooking = null
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { siteId } = useParams();

  // Update form values when modal becomes visible or props change
  useEffect(() => {
    if (isVisible) {
      if (editingBooking) {
        // Edit mode - pre-fill with booking data
        const startTimeSGT = dayjs(editingBooking.startTimeSGT, 'YYYY-MM-DD HH:mm:ss');
        const endTimeSGT = dayjs(editingBooking.endTimeSGT, 'YYYY-MM-DD HH:mm:ss');

        form.setFieldsValue({
          title: editingBooking.title,
          date: startTimeSGT.startOf('day'),
          startTime: startTimeSGT,
          endTime: endTimeSGT,
          roomIds: editingBooking.roomIds.map(room => room._id),
          picName: editingBooking.picName,
          description: editingBooking.description || '',
        });
      } else {
        // Create mode - use slot defaults
        const date = selectedDate ? dayjs(selectedDate) : dayjs();
        const hour = selectedHour ??9;
        const startTime = dayjs(selectedDate).hour(hour).minute(0).second(0);
        const endTime = dayjs(selectedDate)
          .hour(hour !== 23 ?(hour + 1) % 24:hour %24)
          .minute(hour === 23?59:0)
          .second(0);
        form.setFieldsValue({
          date: date,
          startTime: startTime,
          endTime: endTime,
          roomIds: selectedRoom ?? [],
          title: '',
          picName: '',
          description: '',
        });
      }
    }
  }, [isVisible, selectedRoom, selectedDate, selectedHour, editingBooking, form]);

  const handleSubmit = async (values) => {
    try {
      const startDateTime = values.date
        .hour(values.startTime.hour())
        .minute(values.startTime.minute())
        .second(0);

      const endDateTime = values.date
        .hour(values.endTime.hour())
        .minute(values.endTime.minute())
        .second(0);

      const bookingData = {
        title: values.title,
        siteId: siteId,
        startTime: values.startTime.format('YYYY-MM-DD HH:mm:ss'),
        endTime: values.endTime.format('YYYY-MM-DD HH:mm:ss'),
        picName: values.picName,
        description: values.description || '',
        roomIds: values.roomIds,
      };

      let result;
      if (editingBooking) {
        // Update existing booking
        result = await dispatch(updateBooking({
          bookingId: editingBooking._id,
          ...bookingData
        }));
      } else {
        // Create new booking
        result = await dispatch(createBooking(bookingData));
      }

      if (result.success) {
        form.resetFields();
        onSubmit && onSubmit();
        onCancel();
      }
    } catch (error) {
      console.error('Error saving booking:', error);
    }
  };

  const handleDelete = async () => {
    if (editingBooking) {
      try {
        const result = await dispatch(deleteBooking(editingBooking._id, siteId));

        if (result.success) {
          form.resetFields();
          onSubmit && onSubmit();
          onCancel();
        }
      } catch (error) {
        console.error('Error deleting booking:', error);
      }
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  // Format room options for Select
  const roomOptions = rooms.map(room => ({
    label: room.name,
    value: room._id,
  }));

  return (
    <Modal
      title={<span style={{ fontWeight: 'bold' }}>
        {editingBooking ? 'Edit Booking' : 'Add New Booking'}
      </span>}
      open={isVisible}
      onCancel={handleCancel}
      footer={null}
      width={600}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        style={{ marginTop: 20 }}
      >
        <Form.Item
          label={<span style={{ fontWeight: 'bold' }}>Title</span>}
          name="title"
          rules={[{ required: true, message: 'Please enter booking title!' }]}
        >
          <Input placeholder="Enter booking title" />
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: 'bold' }}>Date</span>}
          name="date"
          rules={[{ required: true, message: 'Please select date!' }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            format="DD-MM-YYYY"
          />
        </Form.Item>

        <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
          <div style={{ flex: 1, marginTop: '12px' }}>
            <Form.Item
              label={<span style={{ fontWeight: 'bold' }}>Start Time</span>}
              name="startTime"
              rules={[{ required: true, message: 'Please select start time!' }]}
              style={{ marginBottom: 0 }}
            >
              <TimePicker
                style={{ width: '100%' }}
                format="HH:mm"
                minuteStep={15}
              />
            </Form.Item>
          </div>

          <div style={{ flex: 1, marginTop: '12px' }}>
            <Form.Item
              label={<span style={{ fontWeight: 'bold' }}>End Time</span>}
              name="endTime"
              rules={[{ required: true, message: 'Please select end time!' }]}
              style={{ marginBottom: 0 }}
            >
              <TimePicker
                style={{ width: '100%' }}
                format="HH:mm"
                minuteStep={15}
              />
            </Form.Item>
          </div>
        </div>

        <Form.Item
          label={<span style={{ fontWeight: 'bold' }}>Room(s)</span>}
          name="roomIds"
          rules={[{ required: true, message: 'Please select at least one room!' }]}
        >
          <Select
            mode="multiple"
            placeholder="Select room(s)"
            options={roomOptions}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: 'bold' }}>Person in Charge</span>}
          name="picName"
          rules={[{ required: true, message: 'Please enter person in charge!' }]}
        >
          <Input placeholder="Enter name of person in charge" />
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: 'bold' }}>Description</span>}
          name="description"
        >
          <TextArea
            rows={3}
            placeholder="Enter booking description (optional)"
          />
        </Form.Item>

        <Form.Item style={{ marginTop: 30 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {editingBooking && (
                <Button
                  danger
                  onClick={handleDelete}
                  style={{ marginRight: 8 }}
                >
                  Delete
                </Button>
              )}
            </div>
            <div>
              <Button onClick={handleCancel} style={{ marginRight: 8 }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" style={{backgroundColor: '#64D5D2'}}>
                {editingBooking ? 'Update Booking' : 'Create Booking'}
              </Button>
            </div>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddBookingModal;
